"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FakeUsersClient = void 0;
const UserFactory_1 = require("../../../test-support/UserFactory");
const BoclipsApiErrorFactory_1 = require("../../../test-support/BoclipsApiErrorFactory");
const Account_1 = require("../../accounts/model/Account");
const uuid_1 = require("uuid");
class FakeUsersClient {
    constructor() {
        this.activeUserIds = [];
        this.currentUser = UserFactory_1.UserFactory.sample();
        this.users = [];
        this.usersToPermissionsMap = {};
        this.usersToRoles = {};
        this.activationResponse = true;
    }
    isUserActive(id) {
        return Promise.resolve(this.activeUserIds.includes(id));
    }
    insertActiveUserId(id) {
        this.activeUserIds.push(id);
    }
    insertCurrentUser(user) {
        this.currentUser = user;
    }
    clear() {
        this.activeUserIds = [];
        this.users = [];
        this.currentUser = UserFactory_1.UserFactory.sample();
        this.activationResponse = true;
    }
    setUserAccessExpiresOn(userId, expiryDate) {
        this.users
            .filter((user) => user.id === userId)
            .forEach((user) => (user.accessExpiresOn = expiryDate));
    }
    getCurrentUser() {
        return Promise.resolve(this.currentUser);
    }
    setCurrentUserFeatures(features) {
        this.currentUser.features = features;
    }
    createUser(createUserRequest) {
        if (createUserRequest.lastName === '' ||
            createUserRequest.firstName === '' ||
            createUserRequest.email === '') {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 400,
                message: 'Missing email, first name, or last name!',
            }));
        }
        if (!!this.users.find((user) => user.email === createUserRequest.email)) {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 409,
                message: 'User already exists',
            }));
        }
        if ('accountId' in createUserRequest) {
            const newUser = UserFactory_1.UserFactory.sample({
                ...createUserRequest,
                account: {
                    id: createUserRequest.accountId,
                    name: 'test account',
                    type: Account_1.AccountType.STANDARD,
                    createdAt: new Date('2024-03-05T08:38:50.971544Z'),
                },
            });
            this.users.push(newUser);
            this.usersToPermissionsMap[newUser.id] = {
                canOrder: !!createUserRequest.permissions?.canOrder,
                canManageUsers: !!createUserRequest.permissions?.canManageUsers,
            };
            if (createUserRequest.userRoles) {
                this.usersToRoles[newUser.id] = createUserRequest.userRoles;
            }
            return Promise.resolve(newUser);
        }
        else {
            const newUser = UserFactory_1.UserFactory.sample({
                ...createUserRequest,
                account: {
                    id: 'trial-id',
                    name: 'test account',
                    type: Account_1.AccountType.TRIAL,
                    createdAt: new Date('2024-03-05T08:38:50.971544Z'),
                },
            });
            this.users.push(newUser);
            this.usersToPermissionsMap[newUser.id] = {
                canOrder: false,
                canManageUsers: true,
            };
            return Promise.resolve(newUser);
        }
    }
    createTrialUser(createTrialUserRequest) {
        return this.createUser({
            firstName: createTrialUserRequest.firstName,
            lastName: createTrialUserRequest.lastName,
            email: createTrialUserRequest.email,
            permissions: {
                canOrder: false,
                canManageUsers: true,
            },
            accountId: (0, uuid_1.v4)(),
            type: createTrialUserRequest.type,
            country: createTrialUserRequest.country,
        });
    }
    createClassroomUser(createClassroomUserRequest) {
        return this.createUser({
            firstName: createClassroomUserRequest.firstName,
            lastName: createClassroomUserRequest.lastName,
            email: createClassroomUserRequest.email,
            permissions: {
                canOrder: false,
                canManageUsers: true,
            },
            accountId: (0, uuid_1.v4)(),
            type: createClassroomUserRequest.type,
            country: createClassroomUserRequest.country,
        });
    }
    createDistrictUser(createDistrictUserRequest) {
        return this.createUser({
            firstName: createDistrictUserRequest.firstName,
            lastName: createDistrictUserRequest.lastName,
            email: createDistrictUserRequest.email,
            permissions: {
                canOrder: false,
                canManageUsers: true,
            },
            accountId: (0, uuid_1.v4)(),
            type: createDistrictUserRequest.type,
            country: createDistrictUserRequest.country,
            state: createDistrictUserRequest.state,
            ncesDistrictId: createDistrictUserRequest.ncesDistrictId,
            districtPilotInformation: createDistrictUserRequest.districtPilotInformation,
        });
    }
    deleteUser(_userId) {
        return Promise.resolve();
    }
    getAllUsers() {
        return Promise.resolve(this.users);
    }
    getPermissionsOfUser(id) {
        return this.usersToPermissionsMap[id];
    }
    setPermissionsOfUser(id, permissions) {
        this.usersToPermissionsMap[id] = permissions;
    }
    getUserRolesOfUser(id) {
        return this.usersToRoles[id];
    }
    updateSelf(_user, updateUserRequest) {
        this.users.push(this.currentUser);
        return this.updateUser(this.currentUser.id, updateUserRequest).then(() => Promise.resolve(this.users.find((it) => it.id === this.currentUser.id)));
    }
    updateUser(id, updateUserRequest) {
        if (updateUserRequest.disabled) {
            this.users.splice(this.users.findIndex((user) => user.id === id), 1);
            return Promise.resolve(true);
        }
        const userIndex = this.users.findIndex((user) => user.id === id);
        const userExists = userIndex > -1;
        if ((updateUserRequest.audiences ||
            updateUserRequest.desiredContent ||
            updateUserRequest.jobTitle ||
            updateUserRequest.discoveryMethods ||
            updateUserRequest.hasAcceptedTermsAndConditions) &&
            userExists) {
            const user = this.users[userIndex];
            user.jobTitle = updateUserRequest.jobTitle;
            user.desiredContent = updateUserRequest.desiredContent;
            user.audiences = updateUserRequest.audiences;
            user.discoveryMethods = updateUserRequest.discoveryMethods;
            user.hasAcceptedTermsAndConditions =
                updateUserRequest.hasAcceptedTermsAndConditions ||
                    user.hasAcceptedTermsAndConditions;
            this.users[userIndex] = user;
        }
        if (updateUserRequest.firstName && userExists) {
            const user = this.users[userIndex];
            user.firstName = updateUserRequest.firstName;
            this.users[userIndex] = user;
        }
        if (updateUserRequest.lastName && userExists) {
            const user = this.users[userIndex];
            user.lastName = updateUserRequest.lastName;
            this.users[userIndex] = user;
        }
        const newPermissions = {
            canOrder: updateUserRequest.permissions?.canOrder ?? false,
            canManageUsers: updateUserRequest.permissions?.canManageUsers ?? false,
        };
        this.usersToPermissionsMap[id] = newPermissions;
        if (updateUserRequest.userRoles) {
            this.usersToRoles[id] = updateUserRequest.userRoles;
        }
        return Promise.resolve(this.getPermissionsOfUser(id) === newPermissions);
    }
    resendActivationEmail(user) {
        const originalLink = user.links?.sendActivationEmail?.getOriginalLink();
        if (!originalLink)
            throw new Error('Activation link is missing on user');
        return Promise.resolve(this.activationResponse);
    }
    setActivationEmailResponse(response) {
        this.activationResponse = response;
    }
}
exports.FakeUsersClient = FakeUsersClient;
//# sourceMappingURL=FakeUsersClient.js.map